import {
  DEFAULT_WORKOUT_SORT,
  WorkoutFilterAttributes,
  WorkoutQueryState,
  WorkoutSort,
  buildWorkoutQuery,
  parseWorkoutQuery,
} from '../util/workout-query-params'
import React, { ReactNode, createContext, useContext, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

// Re-exported for the many call sites that already import these from the context.
export { DEFAULT_WORKOUT_SORT }
export type { WorkoutFilterAttributes, WorkoutSort } from '../util/workout-query-params'

export type FilterData = {
  workoutFilter?: WorkoutFilterAttributes
  /** Count of filter categories with at least one selection. 0 → sections view, ≥1 → Results view. */
  activeFilterCount: number
  sort: WorkoutSort
  setSort: (sort: WorkoutSort) => void
  /** Completed Workouts visibility. false (default) = Show; true = hide the user's completed workouts. */
  hideCompleted: boolean
  setHideCompleted: (hideCompleted: boolean) => void
  /** Current results page (1-based). Lives in the URL like every other query field. */
  page: number
  /** Set the page. `replace` swaps the history entry (used to auto-correct an out-of-range page). */
  setPage: (page: number, options?: { replace?: boolean }) => void
  /** Clears all filter selections + sort + the Completed toggle. Used by Reset Filters + the empty-state CTA. */
  clear: () => void
  toggleFilterEntry: (entryKey: keyof WorkoutFilterAttributes, value: string) => void
  setFilterEntry: (entryKey: keyof WorkoutFilterAttributes, values: string[]) => void
}

export const FilterContext = createContext<FilterData>({} as FilterData)

export function useFilterContext(): FilterData {
  return useContext(FilterContext)
}

/**
 * The workouts query (filters + sort + Completed toggle + page) lives in the URL search params — the
 * single source of truth (GH-775). State is decoded from the URL on every render; the setters re-encode
 * it. "Refining" actions (filters / sort / Completed toggle) reset to page 1 and `replace` history so
 * tweaking facets doesn't spam the back stack; page changes `push` so Back steps through pages. Leaving
 * `/workouts` drops the query (natural reset), and browser Back restores the prior filtered URL — this
 * replaces the old in-memory reducer + navigate-away `clear()` effect.
 */
export const FilterContextProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { workoutFilter, sort, hideCompleted, page } = useMemo(() => parseWorkoutQuery(searchParams), [searchParams])

  const { clear, setSort, setHideCompleted, setPage, setFilterEntry, toggleFilterEntry } = useMemo(() => {
    // A "refine" derives the next query from the live URL, resets to page 1, and replaces history so
    // tweaking facets doesn't spam the back stack. The functional updater reads `current` to avoid
    // stale-closure races between rapid changes.
    const refine = (transform: (state: WorkoutQueryState) => WorkoutQueryState): void =>
      setSearchParams((current) => buildWorkoutQuery({ ...transform(parseWorkoutQuery(current)), page: 1 }, current), {
        replace: true,
      })

    return {
      clear: () => refine(() => ({ workoutFilter: {}, sort: DEFAULT_WORKOUT_SORT, hideCompleted: false, page: 1 })),
      setSort: (sort: WorkoutSort) => refine((state) => ({ ...state, sort })),
      setHideCompleted: (hideCompleted: boolean) => refine((state) => ({ ...state, hideCompleted })),
      setFilterEntry: (entryKey: keyof WorkoutFilterAttributes, values: string[]) =>
        refine((state) => ({ ...state, workoutFilter: withFilterEntry(state.workoutFilter, entryKey, values) })),
      toggleFilterEntry: (entryKey: keyof WorkoutFilterAttributes, value: string) =>
        refine((state) => ({ ...state, workoutFilter: withToggledEntry(state.workoutFilter, entryKey, value) })),
      // Page changes push history (Back steps through pages); callers pass `replace` to auto-correct
      // an out-of-range page without leaving a dead entry in the back stack.
      setPage: (next: number, options?: { replace?: boolean }) =>
        setSearchParams((current) => buildWorkoutQuery({ ...parseWorkoutQuery(current), page: next }, current), {
          replace: options?.replace ?? false,
        }),
    }
  }, [setSearchParams])

  const activeFilterCount = filterCount(workoutFilter)

  return (
    <FilterContext.Provider
      value={{
        workoutFilter,
        activeFilterCount,
        sort,
        setSort,
        hideCompleted,
        setHideCompleted,
        page,
        setPage,
        toggleFilterEntry,
        setFilterEntry,
        clear,
      }}
    >
      {children}
    </FilterContext.Provider>
  )
}

const filterCount = (filter: WorkoutFilterAttributes | undefined): number =>
  Object.values(filter ?? {}).filter((filterArray) => filterArray.length > 0).length

// Immutable filter-category updates: set values (dropping the key when empty) and toggle one value.
const withFilterEntry = (
  filter: WorkoutFilterAttributes,
  entryKey: keyof WorkoutFilterAttributes,
  values: string[],
): WorkoutFilterAttributes => {
  const next = { ...filter }
  if (values.length) next[entryKey] = values
  else delete next[entryKey]
  return next
}

const withToggledEntry = (
  filter: WorkoutFilterAttributes,
  entryKey: keyof WorkoutFilterAttributes,
  value: string,
): WorkoutFilterAttributes => {
  const current = filter[entryKey] ?? []
  const next = current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]
  return withFilterEntry(filter, entryKey, next)
}
